import { useQuery } from "@apollo/client"
import Big from "big.js"
import { useMemo } from "react"
import { useMarket } from "sdk-react/markets/useMarket"
import { Handlers } from "sdk-react/tools/useHandlers"
import { POLLING_INTERVAL, POSITION_CHANGEDS_QUERY } from "../graphServer/graphQueries"

interface APIPositionChangedsUpdated {
    id: string
    exchangedPositionSize: string
    swappedPrice: string
    timestamp: string
}

interface IPositionChangedsUpdated {
    positionChangeds: APIPositionChangedsUpdated[]
}

type MarketTradeHistoryInitialState = {
    tickerSymbol: string
    length: number
}

export function useMarketTradeHistory({ tickerSymbol, length = 10 }: MarketTradeHistoryInitialState) {
    const { getHandlers } = Handlers.useContainer()
    const { onError } = getHandlers()
    const { market } = useMarket(tickerSymbol)
    const { baseAddress } = market || {}
    const {
        data,
        loading: isLoading,
        error: queryError,
    } = useQuery<IPositionChangedsUpdated>(POSITION_CHANGEDS_QUERY, {
        variables: {
            baseToken: baseAddress,
            first: length,
        },
        pollInterval: POLLING_INTERVAL,
        skip: !baseAddress,
    })

    if (queryError) {
        onError(queryError)
    }

    const tradeHistory = useMemo(() => {
        if (!data) {
            return []
        }

        const trades = data.positionChangeds

        return trades.map((trade: APIPositionChangedsUpdated) => {
            const txId = trade.id.split("-")[0] // NOTE: <tx hash>-<tx log index>

            return {
                txId,
                exchangedPositionSize: new Big(trade.exchangedPositionSize),
                swappedPrice: new Big(trade.swappedPrice),
                timestamp: +trade.timestamp,
            }
        })
    }, [data])

    return {
        isLoading,
        tradeHistory,
    }
}
